import { pdf } from "@react-pdf/renderer";
import { useQueryClient } from "@tanstack/react-query";
import { isEqual } from "lodash-es";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { compositeCrud } from "@/api/composite";
import { Composite } from "@/api/composite/models";
import { talentCrud } from "@/api/talent/actions";
import { Talent } from "@/api/talent/models";
import { Button, LoadingSpinner } from "@/components/common";
import { InputDropdown } from "@/components/common/inputs";
import { ImportedPhotoData, PhotoImportModal } from "@/components/features/talent/modals/PhotoImportModal";
import { CompositeTypeEnum } from "@/enums/composite";
import { useDepartmentNavigate } from "@/hooks/common/use-department-navigate";
import { useFetch } from "@/hooks/common/use-fetch";
import { useForm } from "@/hooks/common/use-form";
import { showToast } from "@/providers";
import { getShortName } from "@/utils/features/talent/names";
import { verifyIfTalentIsAdult } from "@/utils/features/talent/validations";
import { CompositePdf } from "../CompositePdf";
import { CompositePhotoSlots } from "../CompositePhotoSlots";

interface CompositeFormValues {
  template: string;
  photo1: ImportedPhotoData | null;
  photo2: ImportedPhotoData | null;
  photo3: ImportedPhotoData | null;
  photo4: ImportedPhotoData | null;
}

interface CompositeEditorProps {
  mode: "create" | "edit";
  onCancel: () => void;
}

type TemplateOption = {
  label: string;
  value: string;
  layout: "adult" | "child";
  maxSlots: number;
};

const templateOptions: TemplateOption[] = [
  { label: "Baby", value: CompositeTypeEnum.BABY, layout: "child", maxSlots: 3 },
  { label: "Vogue - Adulto", value: CompositeTypeEnum.VOGUE_ADULTO, layout: "adult", maxSlots: 4 },
  { label: "Vogue - Infantil", value: CompositeTypeEnum.VOGUE_INFANTIL, layout: "child", maxSlots: 3 },
  { label: "Younique - Adulto", value: CompositeTypeEnum.YOUNIQUE_ADULTO, layout: "adult", maxSlots: 4 },
  { label: "Younique - Infantil", value: CompositeTypeEnum.YOUNIQUE_INFANTIL, layout: "child", maxSlots: 3 },
];

export const CompositeEditor = ({ mode, onCancel }: CompositeEditorProps) => {
  const params = useParams<{ id: string; compositeId: string }>();
  const talentId = params.id;
  const urlCompositeId = params.compositeId;
  const navigate = useDepartmentNavigate();
  const isEditMode = mode === "edit";
  const queryClient = useQueryClient();

  const [activeSlot, setActiveSlot] = useState<keyof CompositeFormValues | null>(null);
  const [initialValues, setInitialValues] = useState<CompositeFormValues | null>(null);

  const hasLoadedData = useRef(false);

  const { data: talent, isPending: isLoadingTalent } = useFetch<Talent>(() => talentCrud.getOne(talentId as string), {
    queryKey: ["talent", talentId],
    enabled: !!talentId,
  });

  const { data: existingComposite, isPending: isLoadingComposite } = useFetch<Composite>(
    () => compositeCrud.getOne(urlCompositeId!, { relations: ["photos", "photos.photo"] }),
    {
      queryKey: ["composite", urlCompositeId],
      enabled: isEditMode && !!urlCompositeId,
    },
  );

  const isAdult = useMemo(() => {
    if (talent?.birthday) {
      return verifyIfTalentIsAdult(talent.birthday);
    }
    return false;
  }, [talent]);

  const { action, control, isSubmitting } = useForm<CompositeFormValues, any>({
    handleSubmit: async (data): Promise<any> => {
      const currentTemplate = templateOptions.find((t) => t.value === data.template);
      const maxSlots = currentTemplate?.maxSlots || 4;

      const pdfPreviewData = {
        template: data.template,
        photo1: data.photo1?.url || "",
        photo2: data.photo2?.url || "",
        photo3: data.photo3?.url || "",
        photo4: maxSlots >= 4 ? data.photo4?.url || "" : "",
      };

      if (!talent) {
        throw new Error("Dados do talento não carregados.");
      }

      const blob = await pdf(<CompositePdf data={pdfPreviewData} talent={talent} isAdultLayout={isAdult} />).toBlob();

      const formData = new FormData();
      formData.append("talentId", talentId!);
      formData.append("compositeType", data.template);

      const slots: { slot: number; photoId: string | null }[] = [];
      const photoKeys = ["photo1", "photo2", "photo3", "photo4"] as const;

      photoKeys.forEach((key, index) => {
        const slotNumber = index + 1;
        if (slotNumber <= maxSlots) {
          const photoValue = data[key];
          if (photoValue) {
            if (photoValue.file) {
              slots.push({ slot: slotNumber, photoId: null });
              formData.append("photosToUpload", photoValue.file);
            } else if (photoValue.photoId) {
              slots.push({ slot: slotNumber, photoId: photoValue.photoId });
            }
          }
        }
      });

      formData.append("slots", JSON.stringify(slots));
      const talentShortName = getShortName(talent?.fullName).replace(/\s+/g, "_");
      const fileName = `${talentShortName}_${data.template}_${new Date().getTime()}.pdf`;
      const pdfFile = new File([blob], fileName, { type: "application/pdf" });
      formData.append("pdf", pdfFile);

      if (isEditMode && urlCompositeId) {
        return compositeCrud.update(urlCompositeId, formData);
      } else {
        return compositeCrud.create(formData);
      }
    },
    options: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: ["talent-composites", talentId] });
        await queryClient.invalidateQueries({ queryKey: ["composite", urlCompositeId] });
        showToast({
          type: "success",
          title: `Composite ${isEditMode ? "atualizado" : "criado"} com sucesso!`,
        });

        navigate(`/talentos/${talentId}/composites`);
      },
    },
    fields: {
      template: { validation: "none" },
      photo1: { validation: "none" },
      photo2: { validation: "none" },
      photo3: { validation: "none" },
      photo4: { validation: "none" },
    },
    initialValues: {
      template: "",
      photo1: null,
      photo2: null,
      photo3: null,
      photo4: null,
    },
  });

  const [selectedTemplateValue, photo1, photo2, photo3, photo4] = control.watch([
    "template",
    "photo1",
    "photo2",
    "photo3",
    "photo4",
  ]);

  useEffect(() => {
    if (hasLoadedData.current || !talent) {
      return;
    }

    if (!isEditMode && !selectedTemplateValue) {
      const defaultValue = isAdult ? CompositeTypeEnum.VOGUE_ADULTO : CompositeTypeEnum.BABY;

      control.onChange({ target: { name: "template", value: defaultValue } });
      hasLoadedData.current = true;
      return;
    }

    if (isEditMode && existingComposite && talent && !hasLoadedData.current) {
      const compositeData = existingComposite as any;
      const photosArray = compositeData.photos || [];

      let templateToUse = compositeData.compositeType;
      const isInfantileTemplate = [
        CompositeTypeEnum.BABY,
        CompositeTypeEnum.VOGUE_INFANTIL,
        CompositeTypeEnum.YOUNIQUE_INFANTIL,
      ].includes(templateToUse);

      if (isAdult && isInfantileTemplate) {
        templateToUse = CompositeTypeEnum.VOGUE_ADULTO;
      }

      const findPhotoInSlot = (slotNum: number): ImportedPhotoData | null => {
        const slotData = photosArray.find((s: any) => s.slot === slotNum);
        if (!slotData || !slotData.photo) {
          return null;
        }
        return {
          url: slotData.photo.url,
          photoId: slotData.photo.id,
          isNew: false,
        };
      };

      const loadedValues: CompositeFormValues = {
        template: templateToUse,
        photo1: findPhotoInSlot(1),
        photo2: findPhotoInSlot(2),
        photo3: findPhotoInSlot(3),
        photo4: findPhotoInSlot(4),
      };

      setInitialValues(loadedValues);

      Object.entries(loadedValues).forEach(([key, value]) => {
        control.onChange({ target: { name: key, value } });
      });

      hasLoadedData.current = true;
    }
  }, [existingComposite, isEditMode, control, talent, isAdult, selectedTemplateValue]);

  const hasAtLeastOnePhoto = Boolean(photo1 || photo2 || photo3 || photo4);

  const currentValues = {
    template: selectedTemplateValue,
    photo1,
    photo2,
    photo3,
    photo4,
  };

  const isDirty = useMemo(() => {
    if (!initialValues) {
      return false;
    }

    if (!isEditMode) {
      const emptyValues = {
        template: initialValues.template,
        photo1: null,
        photo2: null,
        photo3: null,
        photo4: null,
      };
      return !isEqual(currentValues, emptyValues);
    }

    return !isEqual(currentValues, initialValues);
  }, [currentValues, initialValues, isEditMode]);

  const handleImportPhoto = (photoData: ImportedPhotoData) => {
    if (activeSlot) {
      control.onChange({ target: { name: activeSlot, value: photoData } });
    }
    setActiveSlot(null);
  };

  if (isLoadingTalent || (isEditMode && isLoadingComposite)) {
    return (
      <div className="flex h-full w-full items-center justify-center p-20">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <>
      <form onSubmit={action} className="form-flow h-full pb-8 flex flex-col">
        <div className="flex w-full flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h3>{isEditMode ? "Editar" : "Criar"} Composite</h3>
          <div className="w-full sm:w-64">
            <InputDropdown
              control={control}
              field="template"
              options={templateOptions.map((opt) => ({
                ...opt,
                disabled: isAdult ? opt.layout === "child" : opt.layout === "adult",
              }))}
              className="w-full"
              buttonClassName="h-11 rounded-sm border-[1px] border-input-border bg-input-background"
            />
          </div>
        </div>

        <CompositePhotoSlots
          isAdultLayout={isAdult}
          slotPhotos={{
            photo1: photo1?.url,
            photo2: photo2?.url,
            photo3: photo3?.url,
            photo4: photo4?.url,
          }}
          onSelectSlot={setActiveSlot}
          onClearSlot={(slot) => {
            control.onChange({ target: { name: slot, value: null } });
          }}
        />

        <div className="flex flex-col sm:flex-row justify-end gap-4 mr-10">
          <Button variant="secondary" onClick={onCancel} type="button">
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
            isLoading={isSubmitting}
            disabled={!hasAtLeastOnePhoto || (isEditMode && !isDirty) || isSubmitting}
          >
            {isEditMode ? "Salvar alterações" : "Salvar composite"}
          </Button>
        </div>
      </form>

      <PhotoImportModal isOpen={activeSlot !== null} onClose={() => setActiveSlot(null)} onImport={handleImportPhoto} />
    </>
  );
};
